import { CSSProperties, useMemo, useState, useCallback } from 'react'

import { RemoveCircleOutline } from '@mui/icons-material'
import { FormHelperText, IconButton, Paper, Stack, TextField, Tooltip } from '@mui/material'

import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import * as OrderTexts from '../../../../../../../../../intl'

import { FieldHeader } from '../FieldHeader'

import { useActions } from './hooks/useActions'

const MainPaperStyle: CSSProperties = {
  background: '#F3F5F7',
  padding: 1,
}

// Use index as key for touched state
type TouchedState = Record<number, boolean>

export interface BarcodeProps {
  testId?: string
  label: string
  maxBarcodes: number
  podsBarcodes: uui.domain.client.rm.BarcodePod[]
}

export type InputValue = string[]
const defaultComponentValue: InputValue = []

export type InputProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = InputValue,
> = {
  barcodeProps: BarcodeProps
} & TidalFormBridgeOptions<InputValue, TargetField, State, AcceptedFormValue>

export function BarcodeField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = InputValue,
>(props: InputProps<TargetField, State, AcceptedFormValue>) {
  const {
    barcodeProps: { testId, label, maxBarcodes, podsBarcodes },
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      value,
      field: { visible },
      onChange,
      fieldDisabled,
      errorOrHelperText,
    },
  } = useTidalFormBridge<InputValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const actions = useActions(value, onChange, podsBarcodes)

  // Track which fields have been touched (blurred) - keyed by index
  const [touched, setTouched] = useState<TouchedState>({})

  const handleBlur = useCallback((index: number) => {
    setTouched(prev => ({
      ...prev,
      [index]: true,
    }))
  }, [])

  // Wrap onRemoveBarcode to also shift touched state when an item is removed
  const handleRemove = useCallback(
    (index: number) => {
      // Shift touched state: items after the removed index move down
      setTouched(prev => {
        const newTouched: TouchedState = {}
        Object.entries(prev).forEach(([idx, state]) => {
          const numIdx = Number(idx)
          if (numIdx < index) {
            newTouched[numIdx] = state
          } else if (numIdx > index) {
            newTouched[numIdx - 1] = state
          }
          // Skip the removed index
        })
        return newTouched
      })
      actions.onRemoveBarcode(index)
    },
    [actions],
  )

  const addButtonDisabled = value.length >= maxBarcodes

  // Parse errorOrHelperText: either (string | null)[] or a plain string
  const { barcodeErrors, generalError } = useMemo(() => {
    if (typeof errorOrHelperText === 'string') {
      try {
        const parsed = JSON.parse(errorOrHelperText) as (string | null)[]
        if (Array.isArray(parsed)) {
          return { barcodeErrors: parsed, generalError: undefined }
        }
        return { barcodeErrors: [] as (string | null)[], generalError: errorOrHelperText }
      } catch {
        // Not valid JSON - treat as general error
        return { barcodeErrors: [] as (string | null)[], generalError: errorOrHelperText }
      }
    }
    return { barcodeErrors: [] as (string | null)[], generalError: undefined }
  }, [errorOrHelperText])

  if (!visible) return null

  return (
    <Stack spacing={2} data-testid={testId}>
      <FieldHeader
        label={label}
        action={{
          label: OrderTexts.getBarcodesAddNewText(),
          tooltip: OrderTexts.getBarcodesAddNewText(),
          disabledTooltip: OrderTexts.getAddBarcodesButtonDisabledText(),
          onClick: actions.onAddBarcode,
          disabled: addButtonDisabled,
        }}
        testId={`${testId}-section-header`}
      />

      {value.length > 0 && (
        <Paper elevation={0} sx={MainPaperStyle}>
          <Stack direction="column" spacing={2}>
            {value.map((barcode, index) => {
              const barcodeError = barcodeErrors[index]
              const isReadonly = actions.readonlyIndexes.has(index)
              // Show error only if field is touched
              const showError = !!barcodeError && !!touched[index]
              return (
                <Stack direction="row" spacing={1} alignItems="flex-start" key={index}>
                  <TextField
                    value={barcode}
                    name="code"
                    label={OrderTexts.getBarcodeFieldLabel()}
                    onChange={e => actions.onUpdateBarcode(index, e.target.value)}
                    onBlur={() => handleBlur(index)}
                    disabled={fieldDisabled || isReadonly}
                    helperText={
                      showError
                        ? barcodeError
                        : isReadonly
                          ? OrderTexts.getReadonlyReasonText()
                          : undefined
                    }
                    error={showError}
                    fullWidth
                    autoFocus={barcode === ''}
                  />
                  <Tooltip title={OrderTexts.getBarcodeRemoveTooltip()}>
                    <IconButton
                      color="error"
                      onClick={() => handleRemove(index)}
                      data-testId={`${testId}-remove-button`}
                      data-trackId={`${testId}-remove-button`}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )
            })}
            {generalError && <FormHelperText error>{generalError}</FormHelperText>}
          </Stack>
        </Paper>
      )}
    </Stack>
  )
}
